<template>
  <div class="my-wishlists">
    <div class="d-flex justify-content-between">
      <h2 class="my-wishlists__heading">
        Wish lists
      </h2>

      <download-csv
        label="Download"
        file-name="Wishlists"
        :format-keys="csv.orders.formatKeys"
        :blacklist-keys="csv.orders.blacklistKeys"
        :input-data="csv.orders.data"></download-csv>
    </div>

    <div class="my-wishlists__items-container">
      <div v-if="loading"
           class="my-wishlists__loading-container">
        <div class="my-wishlists__loading-content">
          <h6>Loading wish lists</h6>
          <font-awesome-icon icon="spinner"
                             class="icon"
                             size="lg"
                             spin></font-awesome-icon>
        </div>
      </div>

      <!-- Only hide the filter bar if the user is not an org admin, as there is only
    one filter that can be used, and that filter will only show if the user is
    an org admin.-->
      <div v-if="isOrgAdmin"
           class="my-wishlists__items-filter">
        <span class="my-wishlists__items-filter-header">
          Filter:
        </span>

        <b-checkbox-group v-model="filterOptions">
          <b-checkbox v-if="isOrgAdmin"
                      value="school-wishlists">
            Wish lists against my school
          </b-checkbox>
          <!-- Filter not used for not -->
          <!-- <b-checkbox value="sent-wishlists">
          Wish lists sent to me
        </b-checkbox> -->
        </b-checkbox-group>

        <button
          class="btn btn-sm refresh-btn"
          :disabled="loading"
          aria-label="Refresh my wish lists"
          @click="loadWishlists">
          <font-awesome-icon icon="sync-alt"
                             class="align-right icon">
          </font-awesome-icon>
          Refresh
        </button>
      </div>

      <template v-if="wishlists && wishlists.length > 0">
        <b-table
          borderless
          responsive
          :fields="fields"
          :items="wishlists"
          class="my-wishlists__items-table custom-table">
          <template v-slot:cell(products)="data">
            {{ data.item.sopLines.length }}
          </template>
          <template v-slot:cell(ordered)="data">
            <template v-if="data.item.statusId === 'PRO'">
              <font-awesome-icon icon="check">
              </font-awesome-icon>
            </template>
            <template v-else>
              no
            </template>
          </template>
          <template v-slot:cell(actions)="data">
            <div class="my-wishlists__actions-container">
              <move-to-basket
                :wish-list-id="data.item.id"
                :wish-list-products="data.item.sopLines"
                class="btn-padding"
                @success="
                  val => {
                    onAddAllToBasketSuccess(val, data.item);
                  }
                "
                @loading="val => (loading = val[0])">
              </move-to-basket>
              <button
                class="btn btn-sm btn-accent-1 btn-padding"
                aria-label="View my wish list"
                @click="onViewClick(data.item.id)">
                View
              </button>
              <button
                class="btn btn-sm btn-accent-1 btn-padding"
                aria-label="Email my wish list"
                @click="onEmailClick(data.item.id)">
                <font-awesome-icon :icon="['far', 'envelope-open-text']">
                </font-awesome-icon>
                Email
              </button>
              <button
                class="btn btn-sm btn-accent-1 btn-padding"
                aria-label="Rename my wish list"
                @click="onRenameClick(data.item.id)">
                Rename
              </button>
              <b-button
                variant="danger"
                class="btn-sm btn-padding"
                aria-label="Delete my wish list"
                @click="onDeleteClick(data.item.id)">
                <span class="visually-hidden">
                  Delete wish list
                </span>
                <font-awesome-icon :icon="['far', 'trash-alt']">
                </font-awesome-icon>
              </b-button>
            </div>
          </template>
        </b-table>

        <div class="my-wishlists__items-cards">
          <wishlist-card
            v-for="(item, index) in wishlists"
            :key="index"
            :wishlist="item"
            @add-to-basket-success="
              val => {
                onAddAllToBasketSuccess(val, item);
              }
            "
            @loading="val => (loading = val[0])"
            @viewClick="onViewClick"
            @emailClick="onEmailClick"
            @deleteClick="onDeleteClick">
          </wishlist-card>
        </div>
      </template>
      <template v-else>
        <div class="my-wishlists__empty">
          <span>No wish lists found.</span>
        </div>
      </template>
    </div>

    <ConfirmModal
      :show="showDeleteConfirmModal"
      heading="Delete wish list"
      @onConfirm="onDeleteConfirm"
      @onClose="showDeleteConfirmModal = false">
      <span>Are you sure you want to delete this wish list?</span>
    </ConfirmModal>
    <EmailWishlistModal
      :show="showEmailWishlistModal"
      :wishlist="selectedWishlist"
      @onClose="showEmailWishlistModal = false">
    </EmailWishlistModal>
    <RenameWishlistModal
      :show="showRenameModal"
      :wishlist-id="selectedWishlist.id"
      :original-title="selectedWishlist.title"
      @onClose="onRenameClose">
    </RenameWishlistModal>
  </div>
</template>

<script>
import { format } from "date-fns";
import {
  listWishlists,
  deleteWishlist,
  isUserOrgAdmin,
  updateWishListStatus
} from "~/Scripts/resources/wishlistResource";
import { getBasketProducts } from "~/Scripts/resources/basketStepResource";
import ConfirmModal from "~/Components/ConfirmModal.vue";
import EmailWishlistModal from "~/Components/Wishlist/EmailWishlistModal.vue";
import MoveToBasket from "~/Components/Wishlist/MoveToBasket.vue";
import WishlistCard from "~/Components/Wishlist/WishlistCard.vue";
import RenameWishlistModal from "~/Components/Wishlist/RenameWishlistModal.vue";
import DownloadCsv from "~/Components/Downloads/DownloadCsv.vue";

export default {
  name: "MyWishlists",
  components: {
    ConfirmModal,
    EmailWishlistModal,
    MoveToBasket,
    WishlistCard,
    RenameWishlistModal,
    DownloadCsv
  },
  data () {
    return {
      wishlists: [],
      filterOptions: ["sent-wishlists"],
      selectedWishlistId: null,
      loading: false,
      showDeleteConfirmModal: false,
      showEmailWishlistModal: false,
      showRenameModal: false,
      isOrgAdmin: false,
      fields: [
        {
          key: "title",
          label: "Name"
        },
        {
          key: "date",
          label: "Date added",
          formatter: this.formatDate
        },
        {
          key: "products",
          label: "Products"
        },
        {
          key: "ordered",
          label: "Ordered"
        },
        {
          key: "actions",
          label: ""
        }
      ]
    };
  },
  computed: {
    selectedWishlist () {
      let result = {};

      if ( this.selectedWishlistId && this.wishlists && this.wishlists.length > 0 ) {
        result = this.wishlists.find( w => w.id === this.selectedWishlistId );
      }

      return result;
    },
    showAllFromOrg () {
      let result = false;
      if ( this.isOrgAdmin ) {
        result = this.filterOptions.includes( "school-wishlists" );
      }
      return result;
    },
    csv () {
      const csvWishlists = [];

      this.wishlists.forEach( wishlist => {
        const csvWishlist = {};

        const {
          title, dateAdded, statusId, sopLines
        } = wishlist;

        csvWishlist.title = title;
        csvWishlist.dateAdded = dateAdded;
        csvWishlist.Ordered = statusId === "PRO" ? "Yes" : "No";

        sopLines.forEach( sop => {
          const {
            productDisplayName, quantity, priceExcVat, priceIncVat
          } = sop;

          const mutableCsvWishlist = { ...csvWishlist };

          mutableCsvWishlist.productName = productDisplayName;
          mutableCsvWishlist.quantity = quantity;
          mutableCsvWishlist.priceExcludingVAT = priceExcVat;
          mutableCsvWishlist.priceIncludingVAT = priceIncVat;

          csvWishlists.push( mutableCsvWishlist );
        } );
      } );

      const csvFormatKeys = [
        { key: "dateAdded", type: "date" },
        { key: "priceExcludingVAT", type: "currency" },
        { key: "priceIncludingVAT", type: "currency" }
      ];

      return { orders: { data: csvWishlists, formatKeys: csvFormatKeys, blacklistKeys: [] } };
    }
  },
  watch: {
    filterOptions: {
      deep: true,
      handler () {
        this.loadWishlists();
      }
    }
  },
  created () {
    this.loading = true;

    isUserOrgAdmin()
      .then( result => {
        if ( result ) {
          this.isOrgAdmin = result.data;
        }
      } )
      .catch( error => {
        console.log( "error checking is user is org admin", error );
        throw error;
      } )
      .finally( () => {
        this.loadWishlists();
      } );
  },
  methods: {
    loadWishlists () {
      this.loading = true;
      listWishlists( true, this.showAllFromOrg )
        .then( result => {
          const { data } = result;
          if ( data ) {
            this.wishlists = data;
          }
        } )
        .catch( error => {
          console.log( "error loading wishlists" );
          throw error;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    onDeleteConfirm () {
      this.showDeleteConfirmModal = false;

      if ( this.selectedWishlistId && this.wishlists ) {
        this.loading = true;

        deleteWishlist( this.selectedWishlistId )
          .then( result => {
            if ( result ) {
              this.loadWishlists();
            }
          } )
          .catch( error => {
            console.log( "error deleting wishlist" );
            throw error;
          } )
          .finally( () => {
            this.loading = false;
            this.selectedWishlistId = null;
          } );
      }
    },
    onDeleteClick ( id ) {
      this.selectedWishlistId = id;
      this.showDeleteConfirmModal = true;
    },
    onViewClick ( id ) {
      window.location.href = `/wishlists/${id}`;
    },
    onEmailClick ( id ) {
      this.selectedWishlistId = id;
      this.showEmailWishlistModal = true;
    },
    onRenameClick ( id ) {
      this.selectedWishlistId = id;
      this.showRenameModal = true;
    },
    onRenameClose ( newTitle ) {
      if ( newTitle ) {
        this.selectedWishlist.title = newTitle;
      }
      this.showRenameModal = false;
    },
    formatDate ( input ) {
      return format( new Date( input ), "dd/MM/yyyy" );
    },
    onAddAllToBasketSuccess ( payload, wishlist ) {
      this.checkWishListStatus( wishlist );
    },
    async checkWishListStatus ( wishlist ) {
      const { id } = wishlist;
      this.selectedWishlistId = id;

      if ( this.selectedWishlist.statusId === "PRO" ) {
        return;
      }

      try {
        this.loading = true;
        const result = await getBasketProducts();

        const { data: basketProducts } = result || {};
        const { sopLines: wishListProducts } = wishlist || {};

        if ( !basketProducts || !wishListProducts ) {
          console.warn( "One or more baskets have no data to compare" );
          return;
        }

        const basketProductIds = basketProducts.map( p => p.Id );

        // only complete the order if every item of the wishlist is in the basket
        const orderCompleted = wishListProducts.length > 0
          ? wishListProducts.every( p => basketProductIds.includes( p.productId ) )
          : true;

        if ( orderCompleted ) {
          updateWishListStatus( this.selectedWishlistId, "PRO" ).then( () => {
            this.selectedWishlist.statusId = "PRO";
          } );
        }
      } catch ( ex ) {
        console.warn( ex );
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="less">
.my-wishlists {
  padding-bottom: 20px;

  .align-right {
    float: right;
  }

  &__loading-container {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    position: absolute;
    z-index: 100;
    pointer-events: none;
    background-color: rgba(255, 255, 255, 0.7);
  }

  &__loading-content {
    display: flex;
    align-items: center;
    h6 {
      margin-bottom: 0;
    }

    .icon {
      margin: 10px;
    }
  }

  &__heading {
    letter-spacing: normal;
    font-weight: bold;
    font-size: 16pt;
    margin-top: 0;
    margin-bottom: 30px;
  }

  &__items-container {
    max-height: 100%;
    overflow-x: auto;
    position: relative;
  }

  &__items-filter {
    width: 100%;
    background-color: #fff;
    padding: 15px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 2px solid #b2d6d9;

    &-header {
      font-weight: 600;
      color: #0c499c;
      margin-right: 15px;
    }

    .refresh-btn {
      display: flex;
      flex-grow: 1;
      justify-content: flex-end;
      align-items: center;
      background-color: #fff;
      color: black;

      &:hover {
        color: gray;
      }

      .icon {
        margin-right: 5px;
      }
    }
  }

  &__items-table {
    @media screen and (max-width: 991px) {
      display: none;
    }

    background-color: #fff;
  }

  &__items-cards {
    @media screen and (min-width: 992px) {
      display: none;
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
  }

  &__actions-container {
    float: right;
    display: flex;
    flex-wrap: wrap;

    button {
      display: inline-flex;
      justify-content: center;
      align-items: center;

      > svg {
        margin-right: 0.25rem;
      }

      &.btn-danger {
        > svg {
          margin-right: 0;
        }
      }
    }
  }

  .btn-padding {
    margin-top: 2px;
    margin-bottom: 2px;
    margin-right: 5px;
  }
}
</style>
